import React from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDSnackbar from "components/MDSnackbar";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  Box,
  Typography,
  Fade,
} from "@mui/material";
import usersTableData from "layouts/users/data/usersTableData";

function Users() {
  const { t } = useTranslation();
  const {
    columns,
    rows,
    activeCount,
    pendingCount,
    testCount,
    blockedCount,
    isModalOpen,
    handleCloseModal,
    handleSaveChanges,
    editedUser,
    setEditedUser,
    notification,
    handleCloseNotification,
  } = usersTableData();
  const statCards = [
    {
      title: t("users.allUsers"),
      count: activeCount + pendingCount + blockedCount + testCount,
      color: "dark",
      icon: "people",
    },
    {
      title: t("users.pendingUsers"),
      count: pendingCount,
      color: "warning",
      icon: "timer",
    },
    {
      title: t("users.activeUsers"),
      count: activeCount,
      color: "success",
      icon: "done",
    },
    {
      title: t("users.testUsers"),
      count: testCount,
      color: "info",
      icon: "done",
    },
    {
      title: t("users.blockedUsers"),
      count: blockedCount,
      color: "error",
      icon: "block",
    },
  ];
  const formFields = [
    {
      name: "state",
      label: t("users.userState"),
      value: editedUser?.state || "",
      options: [
        t("users.states.active"),
        t("users.states.pending"),
        t("users.states.test"),
        t("users.states.blocked"),
      ],
    },
    {
      name: "role",
      label: t("users.userRole"),
      value: editedUser?.role || "",
      options: [
        t("users.roles.customer"),
        t("users.roles.agent"),
        t("users.roles.operator"),
        t("users.roles.admin"),
      ],
    },
  ];

  const selectFieldStyles = {
    marginTop: "1px",
    padding: "11px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: "6px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0, 0, 0, 0.3)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Statistics Cards */}
        <Grid container spacing={3} marginBottom={5}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.title}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color={card.color}
                  icon={card.icon}
                  title={card.title}
                  count={card.count}
                  percentage={{
                    color: "success",
                    amount: "",
                    label: t("users.justUpdated"),
                  }}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        {/* Users Table */}
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("users.title")}
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Edit User Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
          {t("users.updateUserInfo")}
        </DialogTitle>

        <DialogContent>
          {editedUser && (
            <Box display="flex" flexDirection="column" gap={2}>
              {formFields.map((field) => (
                <Box key={field.name}>
                  <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                    {field.label}
                  </Typography>
                  <Select
                    name={field.name}
                    value={field.value}
                    onChange={(e) =>
                      setEditedUser((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      ...selectFieldStyles,
                    }}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSaveChanges} color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("common.success") : t("common.error")}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default Users;
